import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  LinearProgress,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Mic,
  Stop,
  PlayArrow,
  Pause,
  Delete,
  Send,
  FiberManualRecord,
  Edit,
  Refresh
} from '@mui/icons-material';
import { SpeechToTextIcon } from '../icons/SpeechToTextIcon';
import { speechToTextAPI } from '../../services/speechToTextAPI';

interface SpeechToTextDialogProps {
  open: boolean;
  onClose: () => void;
  onTranscriptComplete: (transcript: string) => void;
  defaultLanguage?: string;
}

const SpeechToTextDialog: React.FC<SpeechToTextDialogProps> = ({
  open,
  onClose,
  onTranscriptComplete,
  defaultLanguage = 'en'
}) => {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');

  // Transcription state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [language, setLanguage] = useState(defaultLanguage);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<string>('');

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Language options
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'ar', name: 'Arabic' },
    { code: 'hi', name: 'Hindi' },
    { code: 'ru', name: 'Russian' },
    { code: 'auto', name: 'Auto-detect' }
  ];

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    if (!open) {
      resetRecording();
    }
  }, [open]);

  const resetRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    setIsPlaying(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setTranscript('');
    setIsTranscribing(false);
    setIsEditing(false);
    setError(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl('');
    }
    chunksRef.current = [];
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());

        // Auto-transcribe after recording stops
        handleTranscribe(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Unable to access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  };

  const playRecording = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pausePlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleTranscribe = async (blob?: Blob) => {
    const audioToTranscribe = blob || audioBlob;
    if (!audioToTranscribe) {
      setError('No audio to transcribe');
      return;
    }

    setIsTranscribing(true);
    setError(null);

    try {
      console.log('Transcribing audio...', { language, size: audioToTranscribe.size });
      const result = await speechToTextAPI.transcribeAudio(audioToTranscribe, language, false);

      console.log('Transcription successful:', result);
      setTranscript(result.text);
      setProvider(result.provider);
      setIsEditing(false);
    } catch (err: any) {
      console.error('Transcription error:', err);
      setError(err.message || 'Transcription failed. Please try voice message recording instead.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleRetry = () => {
    if (audioBlob) {
      handleTranscribe(audioBlob);
    }
  };

  const handleSend = () => {
    if (transcript.trim()) {
      onTranscriptComplete(transcript.trim());
      onClose();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <SpeechToTextIcon fontSize="medium" />
              <Typography variant="h6">Speech-to-Text</Typography>
            </Box>
            {provider && (
              <Chip
                label={`Provider: ${provider}`}
                size="small"
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>

            {/* Language Selection */}
            {!isRecording && !audioBlob && (
              <FormControl fullWidth size="small">
                <InputLabel>Language</InputLabel>
                <Select
                  value={language}
                  label="Language"
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Recording Status */}
            <Box sx={{ textAlign: 'center' }}>
              {isRecording && !isPaused && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'center' }}>
                  <FiberManualRecord sx={{ color: 'red', fontSize: '12px' }} />
                  <Typography variant="body2" color="error">
                    Recording...
                  </Typography>
                </Box>
              )}

              {isPaused && (
                <Chip label="Paused" size="small" color="warning" sx={{ mb: 1 }} />
              )}

              {audioBlob && !isRecording && !isTranscribing && !transcript && (
                <Chip label="Recording Complete" size="small" color="success" sx={{ mb: 1 }} />
              )}

              {isTranscribing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'center' }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="primary">
                    Transcribing...
                  </Typography>
                </Box>
              )}

              <Typography variant="h4" sx={{ fontFamily: 'monospace' }}>
                {formatTime(recordingTime)}
              </Typography>
            </Box>

            {/* Recording Progress */}
            {isRecording && (
              <LinearProgress
                variant="indeterminate"
                sx={{ width: '100%' }}
                color={isPaused ? 'warning' : 'primary'}
              />
            )}

            {/* Error Display */}
            {error && (
              <Alert
                severity="error"
                action={
                  audioBlob && (
                    <Button color="inherit" size="small" onClick={handleRetry} startIcon={<Refresh />}>
                      Retry
                    </Button>
                  )
                }
              >
                {error}
              </Alert>
            )}

            {/* Transcription Display/Edit */}
            {transcript && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Transcribed Text:
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setIsEditing(!isEditing)}
                    color={isEditing ? 'primary' : 'default'}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Box>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    variant="outlined"
                    placeholder="Edit transcribed text..."
                  />
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      maxHeight: 200,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    <Typography variant="body1">{transcript}</Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Controls */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
              {!isRecording && !audioBlob && (
                <IconButton
                  onClick={startRecording}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 60,
                    height: 60,
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <Mic fontSize="large" />
                </IconButton>
              )}

              {isRecording && !isPaused && (
                <>
                  <IconButton
                    onClick={pauseRecording}
                    sx={{ bgcolor: 'warning.main', color: 'white', '&:hover': { bgcolor: 'warning.dark' } }}
                  >
                    <Pause />
                  </IconButton>
                  <IconButton
                    onClick={stopRecording}
                    sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <Stop />
                  </IconButton>
                </>
              )}

              {isRecording && isPaused && (
                <>
                  <IconButton
                    onClick={resumeRecording}
                    sx={{ bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } }}
                  >
                    <Mic />
                  </IconButton>
                  <IconButton
                    onClick={stopRecording}
                    sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <Stop />
                  </IconButton>
                </>
              )}

              {audioBlob && !isRecording && !isTranscribing && (
                <>
                  <IconButton
                    onClick={isPlaying ? pausePlayback : playRecording}
                    sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                  >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                  <IconButton
                    onClick={resetRecording}
                    sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <Delete />
                  </IconButton>
                  {!transcript && (
                    <IconButton
                      onClick={() => handleTranscribe()}
                      sx={{ bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } }}
                    >
                      <Refresh />
                    </IconButton>
                  )}
                </>
              )}
            </Box>

            {/* Instructions */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {!isRecording && !audioBlob && 'Click the microphone to start recording'}
              {isRecording && !isPaused && 'Recording... Click pause to pause or stop to finish'}
              {isRecording && isPaused && 'Recording paused. Click mic to resume or stop to finish'}
              {audioBlob && !isRecording && !transcript && isTranscribing && 'Transcribing your audio...'}
              {audioBlob && !isRecording && transcript && 'Review and edit the transcribed text, then click Insert'}
              {audioBlob && !isRecording && !transcript && !isTranscribing && error && 'Transcription failed. Try again or use voice message instead'}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            Cancel
          </Button>
          {transcript && (
            <Button
              onClick={handleSend}
              variant="contained"
              startIcon={<Send />}
              disabled={!transcript.trim()}
            >
              Insert Text
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Hidden audio element for playback */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleAudioEnded}
          style={{ display: 'none' }}
        />
      )}
    </>
  );
};

export default SpeechToTextDialog;
